import { ErrorBoundary } from '@sentry/react';
import { GoogleMapsModal } from 'common/components/GoogleMapsModal/GoogleMapsModal';
import TokenLayout from 'common/components/HolyGrailLayout/TokenLayout';
import { NotFoundView } from 'common/components/NotFound';
import { NotificationContainer } from 'common/components/Notification';
import Splash from 'common/components/Splash';
import { RoleType } from 'common/models';
import { useFrontendRefresh } from 'common/version/useFrontendRefresh';
import { Routes as AuthRoutes } from 'features/auth/Routes';
import { PrivateRoute } from 'features/auth/components/privateRoute.tsx';
import { ConfirmationModal } from 'features/confirmation-modal';
import { EvalEstimatesRoutes } from 'features/evaluation-views/Routes';
import { GrowerRoutes } from 'features/grower-views/Routes';
import { HarvestPayrollRoutes } from 'features/harvest-payroll/Routes';
import { PickPlanRoutes } from 'features/pick-plan-views/Routes';
import { PickScheduleRoutes } from 'features/pick-schedule-views/Routes';
import { SettingsRoutes } from 'features/settings-view/Routes';
import { FC } from 'react';
import { Route, Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Constants } from 'utils/constants';
import AppTheme from 'utils/styleValues';
import { GlobalStyle } from '../GlobalStyle';

export const App: FC = () => {
  // Poll the backend minimum-version endpoint and auto-reload stale sessions.
  useFrontendRefresh();

  return (
    <ErrorBoundary>
      <ThemeProvider theme={AppTheme}>
        <ConfirmationModal />
        <GoogleMapsModal />
        <NotificationContainer />
        <Switch>
          <Route exact path='/' component={Splash} />
          <Route path='/auth' component={AuthRoutes} />
          <TokenLayout>
            <Switch>
              <PrivateRoute
                path={`${Constants.routes.GROWERS_LIST}`}
                component={GrowerRoutes}
                requiredRoles={[
                  RoleType.Admin,
                  RoleType.Analyst,
                  RoleType.FieldRep,
                ]}
              />
              <PrivateRoute
                path={`${Constants.routes.EVAL_EST_MAIN}`}
                component={EvalEstimatesRoutes}
                requiredRoles={[
                  RoleType.Admin,
                  RoleType.Analyst,
                  RoleType.FieldRep,
                  RoleType.HarvestCo,
                ]}
              />
              <PrivateRoute
                path={`${Constants.routes.PICK_PLAN}`}
                component={PickPlanRoutes}
                requiredRoles={[
                  RoleType.Admin,
                  RoleType.Analyst,
                  RoleType.FieldRep,
                  RoleType.HarvestManager,
                ]}
              />
              <PrivateRoute
                path={Constants.routes.PICK_SCHEDULE}
                component={PickScheduleRoutes}
                requiredRoles={[
                  RoleType.Admin,
                  RoleType.Analyst,
                  RoleType.FieldRep,
                  RoleType.HarvestManager,
                  RoleType.HarvestCo,
                ]}
              />
              <PrivateRoute
                path={Constants.routes.HARVEST_PAYROLL}
                component={HarvestPayrollRoutes}
                requiredRoles={[RoleType.Admin]}
              />
              <PrivateRoute
                path={Constants.routes.SETTINGS}
                component={SettingsRoutes}
                requiredRoles={[RoleType.Admin]}
              />
              <Route component={NotFoundView} />
            </Switch>
          </TokenLayout>
          <Route component={NotFoundView} />
        </Switch>
      </ThemeProvider>
      <GlobalStyle />
    </ErrorBoundary>
  );
};
